import React, { useState, useMemo } from 'react';
import { ToggleSwitch, ScopeSelector } from '../../sdk';
import { Hint } from './Field';

const NUMBERING_RE = /^Slide \d+ \/ \d+$/;

/**
 * CTA (Call to Action) editor with This Slide / All Slides scope selector.
 *
 * When scope is "All Slides", toggle and text changes apply to every slide.
 * Uses the same LED scope + engraved divider pattern as GenerativeEditor.
 *
 * @param {Object} props
 * @param {string} props.cta - CTA text
 * @param {string} props.ctaButton - CTA button text
 * @param {boolean} props.footerEnabled - Whether footer is enabled
 * @param {function} props.onChange - Callback with { cta, ctaButton }
 * @param {function} props.onToggle - Callback when footer enabled state changes
 * @param {number} props.slideIndex - Current slide index (0-based)
 * @param {number} props.totalSlides - Total number of slides
 * @param {Array} props.slides - All slides (for diversity check)
 * @param {function} props.onBulkSlideChange - Bulk update callback (patch, hint)
 */
export function CtaEditor({
  cta,
  ctaButton,
  footerEnabled,
  onChange,
  onToggle,
  slideIndex,
  totalSlides,
  slides = [],
  onBulkSlideChange,
}) {
  const isSwipe = cta === 'Swipe →';
  const isNumbering = NUMBERING_RE.test(cta);
  const isNone = !cta && !isSwipe && !isNumbering;

  // ── Scope state (This Slide / All Slides) ──
  const [footerScope, setFooterScope] = useState('slide');

  // Count unique footer states across slides (for diversity warning)
  const uniqueFooterCount = useMemo(() => {
    if (!slides || slides.length <= 1) return 1;
    const states = new Set(slides.map(s => s.footerEnabled !== false));
    return states.size;
  }, [slides]);

  // ── Scope-aware update helpers ──
  const handleToggle = (enabled) => {
    if (footerScope === 'all' && onBulkSlideChange) {
      onBulkSlideChange({ footerEnabled: enabled }, `Footer ${enabled ? 'enabled' : 'disabled'} on all slides`);
    } else {
      onToggle(enabled);
    }
  };

  const handleChange = ({ cta: newCta, ctaButton: newCtaButton }) => {
    if (footerScope === 'all' && onBulkSlideChange) {
      onBulkSlideChange({ cta: newCta, ctaButton: newCtaButton }, 'Footer text updated on all slides');
    } else {
      onChange({ cta: newCta, ctaButton: newCtaButton });
    }
  };

  const showScope = slides.length > 1 && onBulkSlideChange;

  return (
    <div className="field-container">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="module-header">Footer (CTA)</div>
          <div className="module-desc">Website URL and call-to-action.</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {showScope && (
            <>
              <ScopeSelector
                variant="led"
                scope={footerScope}
                onScopeChange={setFooterScope}
                uniqueCount={uniqueFooterCount}
                diversityNoun="footer states"
                confirmMessage={`Slides have different footer settings. Apply current footer state to all ${totalSlides} slides?`}
                onConfirmApply={() => {
                  if (onBulkSlideChange) {
                    onBulkSlideChange(
                      { footerEnabled, cta, ctaButton },
                      'Footer settings applied to all slides'
                    );
                  }
                }}
              />
              <div className="w-px h-5 rounded-full" style={{
                boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.5), 1px 0 0 rgba(255,255,255,0.07)',
              }} />
            </>
          )}
          <ToggleSwitch
            checked={footerEnabled}
            onChange={handleToggle}
            size="sm"
          />
        </div>
      </div>
      {footerEnabled && (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* CTA Text */}
        <div>
          <div className="text-[11px] text-white/55 mb-2">CTA text (right)</div>
          <input
            value={cta || ''}
            onChange={(e) => handleChange({ cta: e.target.value, ctaButton })}
            className="input-base"
            placeholder="Swipe →  /  Slide 2 / 10"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={() => handleChange({ cta: 'Swipe →', ctaButton })}
              className="chip-btn"
            >
              <span
                className="chip-led"
                style={isSwipe ? { background: '#34d399', boxShadow: '0 0 6px #34d399' } : undefined}
              />
              Swipe →
            </button>
            <button
              onClick={() =>
                handleChange({ cta: `Slide ${slideIndex + 1} / ${totalSlides}`, ctaButton })
              }
              className="chip-btn"
            >
              <span
                className="chip-led"
                style={isNumbering ? { background: '#e8b84b', boxShadow: '0 0 6px #e8b84b' } : undefined}
              />
              Slide X / N
            </button>
            <button
              onClick={() => handleChange({ cta: '', ctaButton })}
              className="chip-btn"
            >
              <span
                className="chip-led"
                style={isNone ? { background: '#a78bfa', boxShadow: '0 0 6px #a78bfa' } : undefined}
              />
              None
            </button>
          </div>
        </div>

        {/* Website / Handle */}
        <div>
          <div className="text-[11px] text-white/55 mb-2">Website / handle (left)</div>
          <input
            value={ctaButton || ''}
            onChange={(e) => handleChange({ cta, ctaButton: e.target.value })}
            className="input-base"
            placeholder="magicforest.land"
          />
          <Hint>Shown at bottom left. Default: magicforest.land</Hint>
        </div>
      </div>
      )}
    </div>
  );
}
